import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useChatStore } from "@/stores/chat-store";
import { HELP_IDS } from "@/lib/help-ids";
import { Button } from "@/components/ui/button";
import { HelpCircle, ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface SmartEmptyStateProps {
  title: string;
  description: string;
  ctaLabel: string;
  icon: LucideIcon;
  helpFlowId?: string;
  onCta?: () => void;
  className?: string;
  /** Accent color for the glow — defaults to violet */
  accent?: "violet" | "blue" | "amber" | "emerald" | "rose";
  /** Optional secondary action (e.g. "Help Center" to open the help menu) */
  secondaryCta?: { label: string; onClick: () => void };
}

const ACCENT_STYLES = {
  violet: {
    glow: "bg-violet-500/20",
    iconBg: "bg-violet-500/10 border-violet-500/20",
    iconColor: "text-violet-400",
    btnBg: "bg-violet-500/15 hover:bg-violet-500/25 border-violet-500/20 text-violet-300 hover:text-violet-200",
    ring: "shadow-violet-500/10",
  },
  blue: {
    glow: "bg-blue-500/20",
    iconBg: "bg-blue-500/10 border-blue-500/20",
    iconColor: "text-blue-400",
    btnBg: "bg-blue-500/15 hover:bg-blue-500/25 border-blue-500/20 text-blue-300 hover:text-blue-200",
    ring: "shadow-blue-500/10",
  },
  amber: {
    glow: "bg-amber-500/20",
    iconBg: "bg-amber-500/10 border-amber-500/20",
    iconColor: "text-amber-400",
    btnBg: "bg-amber-500/15 hover:bg-amber-500/25 border-amber-500/20 text-amber-300 hover:text-amber-200",
    ring: "shadow-amber-500/10",
  },
  emerald: {
    glow: "bg-emerald-500/20",
    iconBg: "bg-emerald-500/10 border-emerald-500/20",
    iconColor: "text-emerald-400",
    btnBg: "bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-500/20 text-emerald-300 hover:text-emerald-200",
    ring: "shadow-emerald-500/10",
  },
  rose: {
    glow: "bg-rose-500/20",
    iconBg: "bg-rose-500/10 border-rose-500/20",
    iconColor: "text-rose-400",
    btnBg: "bg-rose-500/15 hover:bg-rose-500/25 border-rose-500/20 text-rose-300 hover:text-rose-200",
    ring: "shadow-rose-500/10",
  },
};

export function SmartEmptyState({
  title,
  description,
  ctaLabel,
  icon: Icon,
  helpFlowId,
  onCta,
  className,
  accent = "violet",
  secondaryCta,
}: SmartEmptyStateProps) {
  const openHelpCenter = useChatStore((s) => s.openHelpCenter);
  const styles = ACCENT_STYLES[accent];

  const handleClick = () => {
    if (helpFlowId) {
      openHelpCenter("wizard", helpFlowId);
    } else if (onCta) {
      onCta();
    }
  };

  const handleOpenLaunchpad = () => {
    if (secondaryCta) {
      secondaryCta.onClick();
    } else {
      openHelpCenter("menu");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={cn("flex items-center justify-center py-20 px-4", className)}
      data-help-id={HELP_IDS.onboarding.emptyState}
    >
      <div className="relative flex flex-col items-center text-center max-w-sm">
        {/* Background glow */}
        <div
          className={cn(
            "pointer-events-none absolute -top-12 left-1/2 -translate-x-1/2 h-48 w-72 rounded-full opacity-60 blur-3xl",
            styles.glow
          )}
        />

        {/* Icon with layered rings */}
        <div className="relative">
          {/* Outer pulse ring */}
          <motion.div
            className={cn(
              "absolute inset-0 rounded-2xl border",
              styles.iconBg
            )}
            animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* Icon container */}
          <div
            className={cn(
              "relative flex h-16 w-16 items-center justify-center rounded-2xl border",
              styles.iconBg
            )}
          >
            <Icon className={cn("h-7 w-7", styles.iconColor)} />
          </div>
        </div>

        {/* Text */}
        <h3 className="mt-6 text-lg font-semibold text-foreground">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>

        {/* CTA buttons */}
        <div className="mt-7 flex items-center gap-3">
          <Button
            onClick={handleClick}
            className={cn(
              "border shadow-lg transition-all duration-200 gap-2",
              styles.btnBg,
              styles.ring
            )}
            variant="ghost"
          >
            {ctaLabel}
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>

          <button
            type="button"
            onClick={handleOpenLaunchpad}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            {secondaryCta?.label ?? "Help Center"}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
